import { NgModule } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';

//Layouts
import { PublicLayoutComponent } from './components/layouts/public-layout/public-layout.component';
import { SecureLayoutComponent } from './components/layouts/secure-layout/secure-layout.component';
import { ErrorLayoutComponent } from './components/layouts/error-layout/error-layout.component';
import { SignupLayoutComponent } from './components/layouts/signup-layout/signup-layout.component';

//Shared
import { AccountMenuComponent } from './components/shared/account-menu.component';
import { FooterComponent } from './components/shared/footer/footer.component';
import { ModalComponent } from './components/shared/modal/modal.component';

//Dashboard Home
import { HomeComponent } from './components/home/home.component';

import { AppComponent } from './app.component';

//Public
import { LoginComponent } from './components/public/login/login.component';
import { ForgottenComponent } from './components/public/forgotten/forgotten.component';
import { ResetPasswordComponent } from './components/public/resetpassword/resetpassword.component';
import { ResetPasswordSuccessComponent } from './components/public/resetpasswordsuccess/resetpasswordsuccess.component';
import { SignupComponent } from './components/public/signup/signup.component';
import { SignupVerifiedComponent } from './components/public/verification/signupverified.component';
import { PageNotFoundComponent } from './components/error/pagenotfound/pagenotfound.component';

//Secure - account
import { PersonalDetailsComponent } from './components/account/personal-details/personal-details.component';
import { SettingsComponent } from './components/account/settings/settings.component';
import { SessionOptionsComponent } from './components/account/session-options/session-options.component';

//Resolvers
import { LanguageResolve } from './resolvers/language.resolve';
import { AccountDataResolve } from './resolvers/accountdata.resolve';
import { WhoAmIResolve } from './resolvers/whoami.resolve';
import { GlobalSettingsResolve } from './resolvers/globalsettings.resolve';
import { NationalityResolve } from './resolvers/nationality.resolve';
import { CountryResolve } from './resolvers/country.resolve';
import { StateResolve } from './resolvers/state.resolve';
import { ReferenceDataService } from './services/referencedata.service';
import { SignupService } from './services/signup.service';
import { AuthGuard } from './guards/auth.guard';
import { AuthService } from './services/auth.service';
import { AccountService } from './services/account.service';
import { CounsellingTopicResolve } from './resolvers/counsellingtopic.resolve';
import { SelectAddToListComponent } from './components/controls/select-add-to-list.component';
import { SelectWorkingHoursComponent } from './components/controls/select-working-hours.component';
import { SessionTypePipe } from './pipes/sessiontype.pipe';
import { DurationPipe } from './pipes/duration.pipe';
import { NewlinePipe } from './pipes/formatnewlines.pipe';
import { BusinessAuthGuard } from './guards/business-auth.guard';
import { DateInputControlComponent } from './components/controls/date-input-control.component';
import { UnlockAccountComponent } from './components/public/unlock/unlockaccount.component';

//Modules
import { NgxStripeModule } from 'ngx-stripe';
import { environment } from '../environments/environment';
import { ClientsComponent } from './components/account/clients/clients.component';
import { ContractsComponent } from './components/account/contracts/contracts.component';
import { ProfessionalsComponent } from './components/account/professionals/professionals.component';
import { IssueCreditComponent } from './components/account/issue-credit/issue-credit.component';
import { TextAddToListComponent } from './components/controls/text-add-to-list.component';

@NgModule({
  declarations: [
    PublicLayoutComponent,
    SecureLayoutComponent,
    ErrorLayoutComponent,
    SignupLayoutComponent,
    AccountMenuComponent,
    AppComponent,
    HomeComponent,
    LoginComponent,
    ForgottenComponent,
    ResetPasswordComponent,
    ResetPasswordSuccessComponent,
    SignupComponent,
    SignupVerifiedComponent,
    PageNotFoundComponent,
    PersonalDetailsComponent,
    SettingsComponent,
    SessionOptionsComponent,
    SelectAddToListComponent,
    TextAddToListComponent,
    SelectWorkingHoursComponent,
    SessionTypePipe,
    DurationPipe,
    NewlinePipe,
    FooterComponent,
    ModalComponent,
    DateInputControlComponent,
    UnlockAccountComponent,
    ClientsComponent,
    ContractsComponent,
    ProfessionalsComponent,
    IssueCreditComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    HttpClientModule,
    FormsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxStripeModule.forRoot(environment.stripeKey),//pk_live_oc4And3zKrzLmnFU43h5REIf 
    RouterModule.forRoot([
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      //Secure Account routing
      {
        path: '',
        component: SecureLayoutComponent,
        children: [
          {
            path: 'dashboard',
            component: HomeComponent,
            resolve: {
              globalSettings: GlobalSettingsResolve,
              whoAmI: WhoAmIResolve,
            },
            canActivate: [AuthGuard]
          },
          {
            path: 'personal-details',
            component: PersonalDetailsComponent,
            resolve: {
              globalSettings: GlobalSettingsResolve,
              whoAmI: WhoAmIResolve,
              countryList: CountryResolve,
              stateList: StateResolve,
              nationalityList: NationalityResolve,
              counsellingTopicList: CounsellingTopicResolve
            },
            canActivate: [AuthGuard]
          },
          {
            path: 'session-options',
            component: SessionOptionsComponent,
            resolve: {
              globalSettings: GlobalSettingsResolve,
              whoAmI: WhoAmIResolve,
            },
            canActivate: [BusinessAuthGuard]
          },
          {
            path: 'clients',
            component: ClientsComponent,
            resolve: {
              globalSettings: GlobalSettingsResolve,
              whoAmI: WhoAmIResolve,
            },
            canActivate: [AuthGuard]
          },
          {
            path: 'contracts',
            component: ContractsComponent,
            resolve: {
              globalSettings: GlobalSettingsResolve,
              whoAmI: WhoAmIResolve,
            },
            canActivate: [AuthGuard]
          },
          {
            path: 'professionals',
            component: ProfessionalsComponent,
            resolve: {
              globalSettings: GlobalSettingsResolve,
              whoAmI: WhoAmIResolve,
            },
            canActivate: [BusinessAuthGuard]
          },
          {
            path: 'issue-credit',
            component: IssueCreditComponent,
          },
          {
            path: 'settings',
            component: SettingsComponent,
            resolve: {
              countryList: CountryResolve,
              stateList: StateResolve,
              globalSettings: GlobalSettingsResolve,
              whoAmI: WhoAmIResolve,
            },
            canActivate: [AuthGuard]
          },
        ],
        resolve: {
          globalSettings: GlobalSettingsResolve,
          whoAmI: WhoAmIResolve,
        },
      },

      //Public routing
      {
        path: '',
        component: PublicLayoutComponent,
        children: [
          { path: 'login', component: LoginComponent },
          { path: 'unlock', component: UnlockAccountComponent },
          { path: 'forgotten', component: ForgottenComponent },
          {
            path: 'resetpwd', component: ResetPasswordComponent,
            resolve: {
              globalSettings: GlobalSettingsResolve
            },
          },
          { path: 'resetpasswordsuccess', component: ResetPasswordSuccessComponent }
        ]
      },

      //Signup routing
      {
        path: '',
        component: SignupLayoutComponent,
        children: [
          {
            path: 'signup',
            component: SignupComponent,
            resolve: {
              countryList: CountryResolve, stateList: StateResolve, nationalityList: NationalityResolve, globalSettings: GlobalSettingsResolve }
          },
          {
            path: 'verification',
            component: SignupVerifiedComponent
          },
          {
            path: 'verification/:token',
            component: SignupVerifiedComponent
          }
        ]
      },

      //Error routing
      {
        path: '',
        component: ErrorLayoutComponent,
        children: [
          { path: '**', component: PageNotFoundComponent }
        ]
      }
    ],
      { onSameUrlNavigation: 'reload' }
    )
  ],
  providers: [{ provide: 'BASE_URL', useFactory: getBaseUrl },
    ReferenceDataService,
    CountryResolve,
    StateResolve,
    NationalityResolve,
    GlobalSettingsResolve,
    CounsellingTopicResolve,
    WhoAmIResolve,
    AccountDataResolve,
    LanguageResolve,
    SignupService,
    AuthGuard,
    AuthService,
    AccountService,
    BusinessAuthGuard
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

export function getBaseUrl() {
  return document.getElementsByTagName('base')[0].href;
}
